import { Flex, Card, Typography } from "antd"
import { ReactNode } from "react"
import { styled, css } from "styled-components"
import { useAnalytics } from "src/global/hooks/analytics/useAnalytics"
import { Icon } from "src/global/ui/Icon"
import { theme } from "src/global/ui/themes/theme"
import Tooltip from "src/global/ui/Tooltip/Tooltip"

const { Text } = Typography

type StatDisplaySetProps = {
  title: string
  info: string
  children: ReactNode
  selected?: boolean
  onClick?: () => void
  clickable?: boolean
}

interface StyledCardProps {
  $isClickable: boolean
  $selected: boolean
}

const StyledCard = styled(Card)<StyledCardProps>`
  ${({ $isClickable, $selected }) => css`
    width: 100%;
    cursor: ${$isClickable ? "pointer" : "default"};
    border: ${$selected ? `1px solid ${theme.colors.primaryColors.gxAccentMedium}` : "none"};
    border-radius: 8px;
    box-shadow: ${$selected
      ? "0 4px 8px 0 rgba(0,0,0,0.08), 0 2px 12px -2px rgba(0,0,0,0.08), 0 2px 4px 0 rgba(0,0,0,0.12)"
      : "0 2px 4px 0 rgba(0,0,0,0.02), 0 1px 6px -1px rgba(0,0,0,0.02), 0 1px 2px 0 rgba(0,0,0,0.03)"};
    transition: all 0.2s ease-in-out;

    ${$isClickable &&
    css`
      &:hover {
        box-shadow:
          0 8px 16px 0 rgb(0 0 0 / 12%),
          0 4px 12px -2px rgb(0 0 0 / 10%),
          0 4px 6px 0 rgb(0 0 0 / 8%);
      }
    `}
  `}
`

const cardStyles = {
  header: {
    backgroundColor: "rgba(0,0,0,0.02)",
    paddingTop: theme.spacing.xxs,
    paddingBottom: theme.spacing.xxs,
    paddingLeft: theme.spacing.s,
    paddingRight: theme.spacing.s,
    borderBottom: "none",
    minHeight: "auto",
  },
  body: {
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.s,
    paddingLeft: theme.spacing.l,
    paddingRight: theme.spacing.l,
  },
}

export function StatDisplaySet({ title, info, selected = false, onClick, clickable, children }: StatDisplaySetProps) {
  const posthog = useAnalytics()
  const onHover = () => {
    posthog?.capture("metric_coverage_stats_tooltip.hover", {
      title,
    })
  }

  const selectable = !!onClick
  const isClickable = clickable ?? selectable // Use clickable prop if provided, otherwise fall back to onClick presence

  return (
    <StyledCard
      size="small"
      styles={cardStyles}
      $isClickable={isClickable}
      $selected={selected}
      data-selected={isClickable && selected}
      title={
        <Flex align="center" justify="center" gap={theme.spacing.xxs}>
          <Text strong>{title}</Text>
          <Tooltip onOpenChange={onHover} title={info}>
            <Icon
              name="infoCircle"
              size="16px"
              lineHeight="0"
              color={theme.colors.neutralColorPalette.blacks.colorTextQuaternary}
            />
          </Tooltip>
        </Flex>
      }
      onClick={onClick}
    >
      <Flex justify="center" align="center">
        {children}
      </Flex>
    </StyledCard>
  )
}
